import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setSession,
  setLoading,
  prefetchCriticalData,
} from "../features/auth/authSlice";
import { onAuthStateChange, getSession, updateLastLogin } from "../services/auth";

/**
 * useAuthSync — Custom hook de sincronización de autenticación.
 *
 * Escucha eventos de sesión globales de forma agnóstica a través de authService,
 * despacha actualizaciones al Redux Store de auth y dispara tareas de pre-carga.
 * Se monta una vez en el componente raíz (App.js).
 */
export default function useAuthSync() {
  const dispatch = useDispatch();
  const { session } = useSelector((state) => state.auth);

  useEffect(() => {
    let isMounted = true;

    // Safety timeout: ensure splash screen NEVER hangs indefinitely under network lag or timeout
    const safetyTimeout = setTimeout(() => {
      if (isMounted) {
        dispatch(setLoading(false));
      }
    }, 3500);

    // 1. Obtener sesión inicial de forma segura
    const initSession = async () => {
      try {
        const { data, error } = await getSession();
        if (error) {
          console.warn("Aviso al obtener sesión inicial:", error);
        }
        const initialSession = data?.session || null;

        if (isMounted) {
          dispatch(setSession(initialSession));
          if (initialSession?.user?.id) {
            dispatch(prefetchCriticalData());
            // No se actualiza last_login en initSession: restaurar una sesión activa no es un nuevo login
          }
        }
      } catch (err) {
        console.warn("Excepción al inicializar sesión:", err);
        if (isMounted) {
          dispatch(setSession(null));
        }
      } finally {
        if (isMounted) {
          dispatch(setLoading(false));
          clearTimeout(safetyTimeout);
        }
      }
    };

    initSession();

    // 2. Escuchar cambios de estado en tiempo real de forma agnóstica
    let unsubscribe = () => {};
    try {
      unsubscribe = onAuthStateChange((event, activeSession) => {
        if (isMounted) {
          dispatch(setSession(activeSession || null));

          if (event === "SIGNED_IN" && activeSession?.user?.id) {
            dispatch(prefetchCriticalData());

            // Evitar registrar logins duplicados si la sesión ya fue sincronizada en esta pestaña/navegador
            const sessionKey = `login_recorded_${activeSession.user.id}`;
            if (!sessionStorage.getItem(sessionKey)) {
              sessionStorage.setItem(sessionKey, Date.now().toString());
              updateLastLogin(activeSession.user.id).catch(console.error);
            }
          }

          if (event === "SIGNED_OUT") {
            try {
              sessionStorage.clear();
            } catch (_) {}
          }
        }
      });
    } catch (err) {
      console.warn("Error al registrar onAuthStateChange:", err);
    }

    return () => {
      isMounted = false;
      clearTimeout(safetyTimeout);
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [dispatch]);

  return session;
}
